import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { CheckCircle, Calendar, DollarSign, Info, Tag, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useConference } from '@/context/ConferenceContext';
import {
  submitRegistration,
  createStripePaymentIntent,
  createPaypalPayment,
  getErrorMessage
} from '@/services/api';

const discountRegistrationSchema = z.object({
  title: z.enum(['Dr', 'Prof', 'Mr', 'Ms', 'Mrs'], { required_error: 'Please select a title' }),
  name: z.string().min(2, 'Full name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number is required'),
  org: z.string().min(2, 'Organization is required'),
  country: z.string().min(2, 'Country is required'),
  discountAmount: z.string().min(1, 'Discount amount is required').refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num > 0;
  }, 'Please enter a valid amount greater than 0'),
});

const DiscountRegistration = () => {
  const {
    conference,
    shortName,
    importantDetails,
    loading: confLoading,
    pricingTier,
    getPricingTierLabel,
    getPricingTierColor,
    isRegistrationOpen,
    siteConfig,
    getImportantDates
  } = useConference();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState('');
  const [paymentProvider, setPaymentProvider] = useState('stripe');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(discountRegistrationSchema),
    defaultValues: {
    },
  });

  const discountAmount = watch('discountAmount') || '0';

  const getDiscountAmount = () => {
    return parseFloat(discountAmount) || 0;
  };

  const calculateTotal = () => {
    let total = getDiscountAmount();

    // Add 5% tax
    const tax = total * 0.05;
    total += tax;

    return Math.max(0, total);
  };

  const onSubmit = async (data) => {
    try {
      // First submit the registration
      const registrationData = {
        title: data.title,
        name: data.name,
        email: data.email,
        phone: data.phone,
        country: data.country,
        address: '', // Add address field if needed
        org: data.org,
        category: 'discount', // Category for discount registration
        conf: shortName,
        user: shortName, // Use shortName as user identifier
        amount: getDiscountAmount(),
      };

      await submitRegistration(registrationData);

      const totalAmount = calculateTotal();

      // Build detailed description
      let descParts = [`Discount Registration: €${getDiscountAmount()}`];
      const subtotal = getDiscountAmount();
      descParts.push(`Tax (5%): €${(subtotal * 0.05).toFixed(2)}`);
      const description = descParts.join(', ');

      const paymentData = {
        title: data.title,
        name: data.name,
        email: data.email,
        phone: data.phone,
        country: data.country,
        address: '', // Add address field if needed
        org: data.org,
        paymentProvider: paymentProvider,
        amount: totalAmount,
        currency: 'EUR',
        conf: shortName,
        category: 'discount', // Category for discount registration
        description,
        successUrl: `${window.location.origin}/payment/success`,
        cancelUrl: `${window.location.origin}/payment/cancel`,
        user: shortName, // Use shortName as user identifier (same as username in database)
      };

      let response;
      if (paymentProvider === 'stripe') {
        response = await createStripePaymentIntent(paymentData);
        // For Stripe, redirect to the URL returned by backend (resp.url)
        if (response.url) {
          window.location.href = response.url;
          return;
        }
      } else if (paymentProvider === 'paypal') {
        response = await createPaypalPayment(paymentData);
        // For PayPal, redirect to approval URL
        if (response.approvalUrl) {
          window.location.href = response.approvalUrl;
          return;
        }
      }

      // Fallback: if no redirect URL, show success
      setRegistrationId(response.id || response.paymentId || 'PENDING');
      setIsSubmitted(true);
      toast.success('Payment initiated successfully!');
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      earlyBird: 'bg-green-500',
      standard: 'bg-yellow-500',
      final: 'bg-red-500',
    };
    return colors[tier] || 'bg-gray-500';
  };

  // Show loading state while conference data loads
  if (confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading conference details...</p>
        </div>
      </div>
    );
  }

  // Show message if registration is closed
  if (!isRegistrationOpen()) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Closed</h2>
          <p className="text-gray-600 mb-6">
            Registration for this conference has closed. The conference date has passed.
          </p>
          <Button onClick={() => window.location.href = '/'}>Return to Home</Button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Discount Registration Successful - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        </Helmet>
        <div className="min-h-screen bg-white flex items-center justify-center py-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex w-20 h-20 rounded-full bg-green-100 items-center justify-center mb-6">
              <CheckCircle className="text-green-500" size={48} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Payment Initiated!</h1>
            <p className="text-lg text-gray-600 mb-6">
              You are being redirected to {paymentProvider === 'stripe' ? 'Stripe' : 'PayPal'} to complete your payment.
            </p>
            <div className="bg-gray-50 p-6 rounded-2xl mb-8">
              <p className="text-sm text-gray-600 mb-2">Your Registration ID</p>
              <p className="text-2xl font-bold text-sky-500" data-testid="registration-id">{registrationId}</p>
            </div>
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 text-left space-y-3">
              <h3 className="font-semibold text-gray-900 text-center mb-4">Next Steps</h3>
              <p className="text-gray-600">
                • Complete your payment on the {paymentProvider === 'stripe' ? 'Stripe' : 'PayPal'} secure checkout page
              </p>
              <p className="text-gray-600">
                • You will be redirected back to our site after payment
              </p>
              <p className="text-gray-600">
                • You will receive a confirmation email once payment is processed
              </p>
              <p className="text-gray-600">
                • Your conference badge and materials will be available at registration desk
              </p>
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              className="mt-8 bg-sky-500 hover:bg-sky-600"
              data-testid="return-home-btn"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Discount Registration - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Discount registration for Civil-CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Discount Registration</h1>
            <p className="text-gray-600">Register for {conference?.confName || siteConfig?.name || 'our conference'} at a discounted rate</p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
              <Tag className="w-4 h-4 text-green-600" />
              <span className="font-semibold text-green-700">Special Discount Offer</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Registration Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="discount-registration-form">
                {/* Personal Information */}
                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Title *</Label>
                      <select
                        id="title"
                        {...register('title')}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                        data-testid="title-select"
                      >
                        <option value="">Select Title</option>
                        <option value="Dr">Dr</option>
                        <option value="Prof">Prof</option>
                        <option value="Mr">Mr</option>
                        <option value="Ms">Ms</option>
                        <option value="Mrs">Mrs</option>
                      </select>
                      {errors.title && (
                        <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        {...register('name')}
                        className="mt-1"
                        data-testid="name-input"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        className="mt-1"
                        data-testid="email-input"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        {...register('phone')}
                        className="mt-1"
                        data-testid="phone-input"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="org">Organization *</Label>
                      <Input
                        id="org"
                        {...register('org')}
                        placeholder="University/Company"
                        className="mt-1"
                        data-testid="org-input"
                      />
                      {errors.org && (
                        <p className="text-red-500 text-sm mt-1">{errors.org.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        {...register('country')}
                        className="mt-1"
                        data-testid="country-input"
                      />
                      {errors.country && (
                        <p className="text-red-500 text-sm mt-1">{errors.country.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Discount Amount */}
                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Discount Amount *</h2>
                  <div className="max-w-md">
                    <Label htmlFor="discountAmount">Enter Discount Amount (€)</Label>
                    <Input
                      id="discountAmount"
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('discountAmount')}
                      placeholder="0.00"
                      className="mt-1"
                      data-testid="discount-amount-input"
                    />
                    {errors.discountAmount && (
                      <p className="text-red-500 text-sm mt-1">{errors.discountAmount.message}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">
                      Enter the discounted registration fee amount (excluding add-ons and tax)
                    </p>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Method *</h2>
                  <RadioGroup
                    value={paymentProvider}
                    onValueChange={setPaymentProvider}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2 p-3 rounded-lg border-2 border-gray-200 hover:border-sky-500 cursor-pointer">
                      <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                      <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                        <div className="font-medium">Credit/Debit Card (Stripe)</div>
                        <div className="text-sm text-gray-500">Secure payment via Stripe</div>
                      </Label>
                    </div>
                    {/* <div className="flex items-center space-x-2 p-3 rounded-lg border-2 border-gray-200 hover:border-sky-500 cursor-pointer">
                      <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                      <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                        <div className="font-medium">PayPal</div>
                        <div className="text-sm text-gray-500">Pay with your PayPal account</div>
                      </Label>
                    </div> */}
                  </RadioGroup>
                </div>

                <div className="flex justify-center">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-green-500 hover:bg-green-600 px-12 py-3 text-lg"
                    data-testid="complete-discount-registration-btn"
                  >
                    {isSubmitting ? 'Processing...' : `Proceed to Payment (${paymentProvider === 'stripe' ? 'Stripe' : 'PayPal'})`}
                  </Button>
                </div>
              </form>
            </div>

            {/* Pricing Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm sticky top-24">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Pricing Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Discount Registration</span>
                    <span className="font-semibold">${getDiscountAmount().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Tax (5%)</span>
                    <span className="font-semibold">${(() => {
                      return (getDiscountAmount() * 0.05).toFixed(2);
                    })()}</span>
                  </div>
                  <div className="flex justify-between py-3 text-xl font-bold text-green-500 border-t-2">
                    <span>Total (incl. tax)</span>
                    <span data-testid="total-amount">${calculateTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-green-50 rounded-xl">
                  <p className="text-sm text-gray-700 font-medium mb-2">Discount Registration Includes:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• All technical sessions</li>
                    <li>• Keynote presentations</li>
                    <li>• Coffee breaks & lunches</li>
                    <li>• Conference materials</li>
                    <li>• Digital proceedings</li>
                    <li>• Welcome reception</li>
                    <li>• Certificate of attendance</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DiscountRegistration;